import React, { useState, useRef } from 'react';
import Input from '../../components/Input/Input';
import './LoginPage.css';
import { useSelector, useDispatch } from 'react-redux';
import {
	setUserCredentials,
	setLoginLoadingStatus,
	selectSessionMessage
} from '../../store/pageLayoutSlice';
import {
	apiCall
} from '../../APICalls/APICalls';

/**
 * Turns a failed sign-in into something worth reading.
 *
 * A bad address and a bad password deliberately produce the same message -
 * the server answers both identically so the form cannot be used to discover
 * which addresses have accounts, and saying more here would give that away.
 */
function describeError(error) {
	if (!error.response) {
		return 'Could not reach the server. Check your connection and try again.';
	}

	const status = error.response.status;
	const data = error.response.data || {};

	if (status === 429) {
		return 'Too many sign-in attempts. Please wait a few minutes and try again.';
	}
	if (status === 423) {
		const minutes = data.retryAfterMinutes;
		return 'This account is temporarily locked after too many failed attempts. '
			+ (minutes ? `Try again in about ${minutes} minute${minutes === 1 ? '' : 's'}.` : 'Try again shortly.');
	}
	if (status === 403 && data.error === 'account_inactive') {
		return data.userStatus === 'Pending'
			? 'This account is awaiting administrator approval.'
			: 'This account is no longer active. Contact an administrator.';
	}
	if (status === 503) {
		return 'Sign-in is temporarily unavailable. Please try again shortly.';
	}
	return 'Invalid email or password.';
}

function LoginPage(props) {

	const dispatch = useDispatch();

	const [ loginEmail, setLoginEmail ] = useState('');
	const [ isValidEmail, setIsValidEmail ] = useState(true);
	const [ errorString, setErrorString ] = useState('');
	const [ isSubmitting, setIsSubmitting ] = useState(false);

	// Recovery is a panel on this screen rather than a separate page, so the
	// user never loses the address they already typed.
	const [ showRecovery, setShowRecovery ] = useState(false);
	const [ recoverySent, setRecoverySent ] = useState(false);
	const [ recoverySubmitting, setRecoverySubmitting ] = useState(false);

	// Explains why the user is back here when a session ended on its own.
	const sessionMessage = useSelector( selectSessionMessage );

	const inputRef = useRef(null);

	const handleEmailChange = e =>{
		setLoginEmail( e.target.value );
	}

	const validateEmail = () =>{
		// An empty field is not an error until submit, and whether an address
		// is a real account is only ever answered by the server.
		setIsValidEmail( true );
	}

	const handleLogin = () =>{
		if ( isSubmitting ) return;

		const password = inputRef.current ? inputRef.current.value : '';

		if ( !loginEmail || !password ) {
			setErrorString('Enter both your email address and password.');
			return;
		}

		setErrorString('');
		setIsSubmitting(true);
		dispatch( setLoginLoadingStatus('loading') );

		apiCall('auth/login', { email: loginEmail, password: password }).then( res => {
			if( res.data && res.data.success === true ){
				// The session itself lives in an httpOnly cookie the browser
				// holds; nothing sensitive is kept in Redux or localStorage.
				dispatch( setUserCredentials( res.data.user ) );
				if ( inputRef.current ) inputRef.current.value = '';
			} else {
				setErrorString('Invalid email or password.');
				dispatch( setLoginLoadingStatus('invalid') );
			}
			setIsSubmitting(false);
		}).catch( error => {
			setErrorString( describeError( error ) );
			dispatch( setLoginLoadingStatus('') );
			setIsSubmitting(false);
		});
	}

	function handleEnterButton(e){
		if(e.keyCode === 13){
			handleLogin();
		}
	}

	/**
	 * Asks the server to mail a reset link.
	 *
	 * The confirmation below is shown whatever comes back - the server answers
	 * identically for an address it knows and one it does not, and undoing
	 * that here by reporting a failure would turn this form into a way to find
	 * out who has an account.
	 */
	const handleRecoveryRequest = () =>{
		if ( recoverySubmitting ) return;

		if ( !loginEmail ) {
			setErrorString('Enter your email address first.');
			return;
		}

		setErrorString('');
		setRecoverySubmitting(true);

		apiCall('auth/requestReset', { email: loginEmail }).then( () => {
			setRecoverySent(true);
			setRecoverySubmitting(false);
		}).catch( () => {
			setRecoverySent(true);
			setRecoverySubmitting(false);
		});
	}

	const message = errorString || sessionMessage;

	return (
		<div id="login_page">
			<div id="login_container">
				<h1 className="login_title">Project Pricing Quote Tool</h1>
				<div className="loginInputContainer">
					{
						message &&
						<span className="login_error">*{ message }</span>
					}
					<Input
						type="text"
						inputId="loginEmail"
						label="Email"
						handleChange={ handleEmailChange }
						handleBlur={ validateEmail }
						value={ loginEmail }
						isValid={ isValidEmail }
						inputClass="loginInput"
						labelClass="loginInputLabel"
						placeHolder="Email"
						handleKeyDown={ (e) => handleEnterButton(e) }
					/>
				</div>
				<div className="loginInputContainer">
					<label className="loginInputLabel" htmlFor="password">
						Password
					</label>
					<input
						type="password"
						id="password"
						className="loginInput"
						placeholder="Password"
						autoComplete="current-password"
						ref={ inputRef }
						onKeyDown={ handleEnterButton }
					/>
				</div>
				<div className="login_button_container">
					<button
						id="SSO_button"
						className="BaseButton"
						onClick={ handleLogin }
						disabled={ isSubmitting }
					>
						{ isSubmitting ? 'Signing in...' : 'Sign In' }
					</button>
				</div>
				<div className="login_recovery">
					{
						!showRecovery &&
						<button
							type="button"
							className="login_recovery_link"
							onClick={ () => setShowRecovery(true) }
						>
							Forgot password?
						</button>
					}
					{
						showRecovery && !recoverySent &&
						<div className="login_recovery_panel">
							<p className="login_recovery_text">
								Enter your email address above and we will send you a link
								to choose a new password. The link can be used once and
								expires in 30 minutes.
							</p>
							<p className="login_recovery_text">
								No email? An administrator can reset your password from the
								Admin Portal and give you a temporary one.
							</p>
							<div className="login_recovery_actions">
								<button
									type="button"
									className="BaseButton login_recovery_send"
									onClick={ handleRecoveryRequest }
									disabled={ recoverySubmitting }
								>
									{ recoverySubmitting ? 'Sending...' : 'Send Reset Link' }
								</button>
								<button
									type="button"
									className="login_recovery_link"
									onClick={ () => setShowRecovery(false) }
								>
									Cancel
								</button>
							</div>
						</div>
					}
					{
						recoverySent &&
						<p className="login_recovery_text login_recovery_sent">
							If an account exists for that address, a reset link is on its
							way. Check your inbox, and your spam folder.
						</p>
					}
				</div>
			</div>
		</div>
	);
}

export default LoginPage;
